import LoadingButton from "@mui/lab/LoadingButton";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
	GridCellParams,
	GridColDef,
	GridRowId,
	GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { TimeLabelGroup } from "@repo/server-api";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";

import { useAllTimeLabelGroupsDataSource } from "../datasources/AllTimeLabelGroupsDataSource";
import List from "../lists/List";

export default function DialogSelectTimeLabels(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		setTimeLabelGroups: (value: TimeLabelGroup[]) => void;
		timeLabelGroups: TimeLabelGroup[];
		contactId: string;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);

	const [timeLabelGroups, setTimeLabelGroups] = useState<TimeLabelGroup[]>([]);
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const allTimeLabelGroupsDataSource = useAllTimeLabelGroupsDataSource();

	useEffect(() => {
		setOpen(props.open);
		if (props.open && props.contactId) {
			allTimeLabelGroupsDataSource.fetchDataForId?.(props.contactId);
		}
	}, [props.open, props.contactId]);

	useEffect(() => {
		if (allTimeLabelGroupsDataSource.rows) {
			setTimeLabelGroups(allTimeLabelGroupsDataSource.rows);
		}
	}, [allTimeLabelGroupsDataSource.rows]);

	useEffect(() => {
		if (!props.timeLabelGroups) return;
		const foundMemberIds: string[] = [];
		props.timeLabelGroups?.forEach((e: TimeLabelGroup) => {
			if (e.id) {
				foundMemberIds.push(e.id);
			}
		});
		setRowSelectionModel(foundMemberIds);
	}, [props.timeLabelGroups]);

	const handleClose = () => {
		setOpen(false);
		props.setOpen(false);
		setSaveClicked(false);
		setRowSelectionModel([]);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		event.preventDefault();
		setSaveClicked(true);

		const selectedGroups = timeLabelGroups.filter((label: TimeLabelGroup) =>
			rowSelectionModel.includes(label.id),
		);

		props.setTimeLabelGroups(selectedGroups);

		handleClose();
	};

	const columns: GridColDef<TimeLabelGroup>[] = [
		{
			field: "name",
			headerName: t("name"),
			width: 250,
			flex: 1,
			renderCell(params: GridCellParams) {
				return params.row.name;
			},
		},
		{
			field: "isGlobal",
			headerName: t("timetracking.timeLabels.tableTimeLabelGroup.isGlobal"),
			width: 250,
			flex: 1,
			renderCell(params: GridCellParams) {
				return params.row.isGlobal ? (
					<span style={{ color: "green" }}>{t("yes")}</span>
				) : (
					<span style={{ color: "red" }}>{t("no")}</span>
				);
			},
		},
	];

	return (
		<Dialog
			open={open}
			PaperProps={{
				component: "form",
				onSubmit: handleSubmit,
			}}
			maxWidth="md"
			fullWidth={true}>
			<DialogTitle>{t("timetracking.timeLabels.dialogTimeLabelGroups.title")}</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						height: "auto",
						width: "100%",
						borderBottom: "1px solid lightgrey",
					}}>
					<List
						dataSource={allTimeLabelGroupsDataSource}
						columns={columns as GridCellParams[]}
						rowSelectionModel={rowSelectionModel as unknown as GridRowSelectionModel}
						onRowSelectionModelChange={(newRowSelectionModel) => {
							setRowSelectionModel((prev) => {
								const filteredIds = allTimeLabelGroupsDataSource.rows.map((row) => row.id);
								const notVisibleSelectedIds = prev.filter(
									(id) => !filteredIds.includes(id as string),
								);
								const newlySelectedIds = (
									Array.isArray(newRowSelectionModel)
										? newRowSelectionModel
										: Array.isArray(newRowSelectionModel)
											? newRowSelectionModel
											: []
								).filter((id) => !prev.includes(id as string));
								return [...notVisibleSelectedIds, ...newlySelectedIds];
							});
						}}
						contactId={props.contactId}>
						{/* <Button
                            data-testid="add-button"
                            onClick={() => {
                                setOpen(true);
                            }}
                            className="blueButton marginLeft20"
                        >
                            {t("timetracking.timeLabels.dialogTimeLabels.add")}
                        </Button> */}
					</List>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					data-testid="dialog-submit-team-member"
					type="submit"
					variant="contained"
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
